import { activityOutcome, toolLabel, toolOutcome, type ToolOutcome } from './tool-presentation';
import {
  ACTIVITY_VERBS,
  connectionReadCopy,
  fallbackActivityVerb,
  localizedActivityVerb,
  type ActivityVerb,
} from './activity-verbs';
import { formatCount } from '@/i18n/format';
import { interpolateCopy } from '@/i18n/context';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import { citationTarget } from '@/citations/reference';
import { STORE_READ_TOOLS } from '@/workspace/storefront/read-request';

export type ActivitySummaryPart = {
  type: string;
  toolName?: string;
  args?: unknown;
  argsText?: string;
  status?: { type: string; reason?: string };
  isError?: boolean;
  result?: unknown;
  text?: string;
  approval?: unknown;
  interrupt?: unknown;
};
export type ActivityPhrase = { key: string; label: string; count: number; verb: ActivityVerb };

const standalone = new Set([
  'ask_user_question',
  'offer_connections',
  'read_external_mcp',
  'update_my_settings',
  'propose_knowledge_change',
  'suggest_page_edits',
  'create_skill_draft',
  'update_skill_draft',
  'save_work_result',
  // The native build approval is a card the person acts on: it stays visible (thread.tsx
  // STANDALONE_TOOL_NAMES keeps the same list).
  'build_artifact',
  'create_agent',
  'update_agent',
  'set_agent_trigger',
  'run_agent',
  'propose_company_knowledge',
  // Storefront: a saved design is its own card, and a paid read or a check of saved work is a
  // request the person approves.
  'change_storefront_design',
  ...STORE_READ_TOOLS,
  // Projects v1: cards the person reads or acts on (Create, Move).
  'draft_project',
  'suggest_project_move',
  'move_chat_to_project',
  'add_to_project',
  'remove_from_project',
  // An App's secret: a secure field the owner types into, never part of the trace.
  'request_app_secret',
]);

export function isActivityPart(part: ActivitySummaryPart) {
  return (
    part.type === 'reasoning' || (part.type === 'tool-call' && !standalone.has(part.toolName ?? ''))
  );
}

function verbFor(name: string, locale = 'en'): ActivityVerb & { key: string; label: string } {
  const known = ACTIVITY_VERBS[name];
  if (known)
    return {
      ...localizedActivityVerb(locale, name, known),
      key: name,
      label: toolLabel(name).label,
    };
  const fallback = fallbackActivityVerb(locale);
  return {
    key: name,
    label: toolLabel(name).label,
    running: `${fallback.running} · ${toolLabel(name).label}`,
    done: `${fallback.done} · ${toolLabel(name).label}`,
    failed: `${fallback.failed ?? fallback.done} · ${toolLabel(name).label}`,
  };
}

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * DES1 HC-06: what one read_connection call actually read, from the turn's own
 * parts only. The name is the connection's own, from a list_connection_reads
 * result in the same turn for that exact connectionId (never guessed); the
 * table is the query plan's relation (`arguments.relation`). Nothing else from the payload is shown.
 */
function connectionRead(part: ActivitySummaryPart, context: readonly ActivitySummaryPart[]) {
  const args = record(callInput(part));
  // The id is opaque: compared exactly as the server resolves it, never trimmed, so an id that
  // differs by a space is a different (unnamed) connection.
  const connectionId =
    typeof args?.connectionId === 'string' && args.connectionId !== '' ? args.connectionId : null;
  if (part.toolName !== 'read_connection' || !connectionId) return null;
  let name: string | null = null;
  for (const listed of context) {
    if (listed.type !== 'tool-call' || listed.toolName !== 'list_connection_reads') continue;
    const items = record(listed.result)?.items;
    if (!Array.isArray(items)) continue;
    const item = items.map(record).find((entry) => entry?.connectionId === connectionId);
    name = text(item?.name) ?? name;
  }
  // The query plan is the call's own `arguments` (flat, as the server parses it).
  const relation = record(record(args?.arguments)?.relation);
  const table =
    args?.operation === 'query' && text(relation?.table)
      ? `${text(relation?.schema) ?? ''}.${text(relation?.table)}`
      : null;
  return { connectionId, name, table };
}

/** A query that actually returned rows: the managed read's own result ({ rows, rowCount, … }). */
function readReturnedRows(part: ActivitySummaryPart) {
  const result = record(part.result);
  return (
    partOutcome(part) === 'complete' &&
    Array.isArray(result?.rows) &&
    typeof result?.rowCount === 'number'
  );
}

/** "Read Synthetic sales database": one phrase per named connection. */
function namedReadVerb(
  name: string,
  connectionId: string,
  locale: string,
): ActivityVerb & { key: string; label: string } {
  const copy = connectionReadCopy(locale);
  const done = interpolateCopy(copy.namedDone, { name });
  return {
    key: `read_connection:${connectionId}`,
    label: done,
    running: interpolateCopy(copy.namedRunning, { name }),
    done,
    failed: interpolateCopy(copy.namedFailed, { name }),
  };
}

function verbForPart(
  part: ActivitySummaryPart,
  locale: string,
  context: readonly ActivitySummaryPart[],
) {
  const read = connectionRead(part, context);
  return read?.name
    ? namedReadVerb(read.name, read.connectionId, locale)
    : verbFor(part.toolName ?? '', locale);
}

/**
 * DES1 HC-12: the same named phrase for one step in Activity → Steps ("Read
 * Synthetic sales database"), or null when the turn discloses no name for it.
 */
export function namedConnectionStep(
  part: ActivitySummaryPart,
  locale: string,
  context: readonly ActivitySummaryPart[],
) {
  const read = connectionRead(part, context);
  return read?.name ? namedReadVerb(read.name, read.connectionId, locale) : null;
}

/** The localized "Couldn't {verb}" of one step, e.g. "Couldn't read a file". */
export function failedActivityLabel(toolName: string, locale = 'en'): string {
  const verb = verbFor(toolName, locale);
  return verb.failed ?? verb.done;
}

const partOutcome = (part: ActivitySummaryPart) =>
  toolOutcome({
    toolName: part.toolName,
    status: part.status as never,
    isError: part.isError,
    result: part.result,
    approval: part.approval,
    interrupt: part.interrupt,
  });

/** The call's input as the tool received it: `args`, else its serialized `argsText`. */
function callInput(part: ActivitySummaryPart): unknown {
  if (part.args !== undefined) return part.args;
  try {
    return part.argsText ? JSON.parse(part.argsText) : null;
  } catch {
    return part.argsText ?? null;
  }
}

/**
 * What a retry repeats. A database read retries the same read on the same
 * connection (same operation and, for a query, the same table) with fresh pins,
 * so its pins are left out; any other step retries the same tool with the same
 * input. The connection id is compared exactly, as HC-06 names it.
 */
function attemptKey(part: ActivitySummaryPart) {
  const input = record(callInput(part));
  const connectionId = input?.connectionId;
  if (part.toolName === 'read_connection' && typeof connectionId === 'string' && connectionId) {
    const relation = record(record(input?.arguments)?.relation);
    return JSON.stringify([
      'read_connection',
      connectionId,
      input?.operation ?? null,
      text(relation?.schema),
      text(relation?.table),
    ]);
  }
  // The writer refuses a save with a stray root title before it runs, and the corrected retry
  // differs only by that title: one save. Every other field (kind, payload, append, update,
  // selection, mediaSelection, replaces) stays in the key, so a different save never covers it.
  if (part.toolName === 'save_artifact' && input) {
    const { title: _rootTitle, ...save } = input;
    return `save_artifact:${JSON.stringify(save)}`;
  }
  // Otherwise only an exact retry counts: changed (or reordered) input is a different step,
  // so its failure keeps the line on failed.
  return `${part.toolName}:${JSON.stringify(callInput(part) ?? null)}`;
}

/**
 * DES1 HC-10: failed calls that a later call of the same thing completed. The
 * turn's outcome is the last attempt's; these stay in the steps as earlier
 * attempts and never make the headline read as failed.
 */
export function earlierAttempts(parts: readonly ActivitySummaryPart[]) {
  const completed = new Set<string>();
  const earlier = new Set<ActivitySummaryPart>();
  for (const part of [...parts].reverse()) {
    // Only activity steps: the line and the trace then count the same calls.
    if (part.type !== 'tool-call' || !part.toolName || !isActivityPart(part)) continue;
    const outcome = partOutcome(part);
    if (outcome === 'complete') completed.add(attemptKey(part));
    else if (outcome === 'error' && completed.has(attemptKey(part))) earlier.add(part);
  }
  return earlier;
}

/**
 * A failed turn names the step that failed, never a neighbour that succeeded
 * (ACTIONS-DESIGN §3.2: "Couldn't read Gmail · 2 steps done"). The completed
 * steps are counted, not named. Null when no activity step failed. `context`
 * is the whole turn, for naming a step (HC-06).
 */
export function activityFailure(
  parts: readonly ActivitySummaryPart[],
  locale = 'en',
  context: readonly ActivitySummaryPart[] = parts,
): { label: string; completed: number } | null {
  const calls = parts.filter(
    (part) => part.type === 'tool-call' && isActivityPart(part) && part.toolName,
  );
  const failed = calls.find((part) => partOutcome(part) === 'error');
  if (!failed?.toolName) return null;
  const verb = verbForPart(failed, locale, context);
  return {
    label: verb.failed ?? verb.done,
    completed: calls.filter((part) => partOutcome(part) === 'complete').length,
  };
}

/**
 * One phrase per step kind, in order. `context` is the whole turn, so a single
 * running part can still be named by an earlier list result.
 */
export function activityPhrases(
  parts: readonly ActivitySummaryPart[],
  locale = 'en',
  context: readonly ActivitySummaryPart[] = parts,
): ActivityPhrase[] {
  const phrases = new Map<string, ActivityPhrase>();
  // Distinct tables actually queried by completed reads, per named connection.
  const tables = new Map<string, Set<string>>();
  for (const part of parts) {
    if (part.type !== 'tool-call' || !isActivityPart(part) || !part.toolName) continue;
    const verb = verbForPart(part, locale, context);
    const read = verb.key.startsWith('read_connection:') ? connectionRead(part, context) : null;
    // Only a query whose result came back counts its table; the plan alone proves nothing.
    if (read?.table && readReturnedRows(part))
      tables.set(verb.key, (tables.get(verb.key) ?? new Set()).add(read.table));
    const previous = phrases.get(verb.key);
    phrases.set(
      verb.key,
      previous
        ? { ...previous, count: previous.count + 1 }
        : { key: verb.key, label: verb.done, count: 1, verb },
    );
  }
  const tablesCount = connectionReadCopy(locale).tablesCount;
  return [...phrases.values()].map((phrase) => {
    if (!phrase.key.startsWith('read_connection:')) return phrase;
    // A named read is one phrase whatever the call count; the tables say how much.
    const queried = tables.get(phrase.key)?.size ?? 0;
    const label = queried
      ? `${phrase.verb.done} · ${formatCount(tablesCount, queried, locale)}`
      : phrase.verb.done;
    return { ...phrase, label, count: 1 };
  });
}

export function activityStatus(parts: readonly ActivitySummaryPart[]): ToolOutcome {
  const calls = parts.filter((part) => part.type === 'tool-call');
  return calls.length
    ? activityOutcome(
        calls.map((part) => ({
          status: part.status as never,
          isError: part.isError,
          result: part.result,
          approval: part.approval,
          interrupt: part.interrupt,
        })),
      )
    : 'complete';
}

export function activityResultCount(result: unknown): number | null {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null;
  const values = Object.values(result as Record<string, unknown>);
  const array = values.find(Array.isArray);
  return Array.isArray(array) ? array.length : null;
}

/** Count only server-shaped citation links in answer text, deduped by target. */
export function answerCitations(parts: readonly ActivitySummaryPart[]): string[] {
  const found = new Map<string, string>();
  for (const part of parts) {
    if (part.type !== 'text' || !part.text) continue;
    const tree = unified().use(remarkParse).parse(part.text) as { children?: unknown[] };
    const visit = (node: unknown) => {
      if (!node || typeof node !== 'object') return;
      const value = node as { type?: string; url?: string; children?: unknown[] };
      if (value.type === 'link' && value.url) {
        const target = citationTarget(value.url, 'https://chat.poneva.com/');
        if (target) {
          // A citation's proof range and digest can change while it still
          // points at the same source. The activity line reports distinct
          // validated sources, so dedupe by the server target identity.
          const hash = new URL(target.href, 'https://chat.poneva.com/').hash;
          const params = new URLSearchParams(hash.slice(hash.indexOf('?') + 1));
          const identity = `${params.get('canvas')}:${params.get('id')}`;
          if (!found.has(identity)) found.set(identity, target.href);
        }
      }
      value.children?.forEach(visit);
    };
    tree.children?.forEach(visit);
  }
  return [...found.values()];
}
